import { createAsyncThunk } from '@reduxjs/toolkit';
import { 
  updateProgress,
  completeProgress,
  startProgress,
} from '../slices/simpleLegalProgressSlice';
import { DBMessageThunks } from './DBServiceThunks';
import feathers from '../../feathers';

// ✅ Simplified MQTT handler for legal query progress
export const handleSimpleLegalQueryProgressUpdate = createAsyncThunk(
  'simpleMqtt/handleLegalQueryProgress',
  async (
    progressData: {
      queryId: string;
      stage: string;
      status: 'active' | 'completed' | 'error';
      progress?: number;
      details?: string;
      subQueryIndex?: number;
      subQueryTotal?: number;
      advancesProgress?: boolean; // ✅ NEW: distinguish update types
    },
    { dispatch, getState }
  ) => {
    const { queryId, stage, status, progress = 0, details = '', advancesProgress = true } = progressData;
    
    const updateType = advancesProgress ? 'Milestone' : 'Feed';
    console.log(`📡 [SIMPLE_MQTT] ${updateType} update: queryId=${queryId}, stage=${stage}, status=${status}, progress=${progress}%`);

    try {
      if (status === 'completed') {
        // ✅ Mark as completed and remove from active list
        dispatch(completeProgress({ queryId }));
        console.log(`✅ [SIMPLE_MQTT] Completed queryId: ${queryId}`);
      } else {
        // ✅ Check if we need to fetch chatId/caseId for auto-creation
        const state = getState() as any;
        const existingProgress = state.simpleLegalProgress?.activeProgresses?.[queryId];
        
        let chatId: string | undefined;
        let caseId: string | undefined;
        
        if (!existingProgress) {
          // Try to find the message with this queryId to get chatId/caseId
          try {
            console.log(`🔍 [SIMPLE_MQTT] Fetching message for queryId: ${queryId}`);
            const messageResult = await dispatch(DBMessageThunks.find({
              'metadata.queryId': queryId,
              $limit: 1,
              $paginate: false
            })).unwrap();
            
            const messages = Array.isArray(messageResult) ? messageResult : messageResult.data || [];
            const message = messages[0];
            
            if (message) {
              chatId = message.chatId;
              caseId = message.caseId;
              console.log(`✅ [SIMPLE_MQTT] Found message: chatId=${chatId}, caseId=${caseId}`);
            } else {
              console.warn(`⚠️ [SIMPLE_MQTT] No message found in ai-legal-messages for queryId: ${queryId}, checking case-documents-search...`);
              
              // Check case-documents-search as fallback
              const searchResult = await feathers.service('case-documents-search').find({
                query: {
                  'metadata.queryId': queryId,
                  $limit: 1
                }
              });
              
              const searchRecords = Array.isArray(searchResult) ? searchResult : (searchResult as any).data || [];
              const searchRecord = searchRecords[0];
              
              if (searchRecord) {
                caseId = searchRecord.caseId;
                // case-documents-search might not have chatId, but that's okay
                console.log(`✅ [SIMPLE_MQTT] Found search record: caseId=${caseId}`);
              } else {
                console.warn(`⚠️ [SIMPLE_MQTT] No record found in case-documents-search either for queryId: ${queryId}`);
              }
            }
          } catch (error) {
            console.error(`❌ [SIMPLE_MQTT] Error fetching message for queryId ${queryId}:`, error);
          }
        }
        
        // ✅ Update progress with server-calculated values and advancesProgress flag
        dispatch(updateProgress({
          queryId,
          overallProgress: progress,
          currentStage: stage,
          stageDetails: details,
          status,
          chatId,
          caseId,
          advancesProgress // ✅ NEW: pass through to reducer
        }));
        console.log(`🔄 [SIMPLE_MQTT] Updated queryId: ${queryId} to ${progress}% (${updateType})`);
      }
    } catch (error) {
      console.error('[SIMPLE_MQTT] Error handling progress update:', error);
    }
  }
);

// ✅ Restore progress from database backup (used after page refresh)
export const restoreProgressFromBackup = createAsyncThunk(
  'simpleMqtt/restoreProgressFromBackup',
  async (
    { messageId }: { messageId: string },
    { dispatch }
  ) => {
    try {
      console.log(`🔄 [BACKUP_RESTORE] Attempting to restore progress for messageId: ${messageId}`);
      
      // Fetch the message from database using find (DBMessageThunks doesn't have a 'get' method)
      const messageResult = await dispatch(DBMessageThunks.find({ _id: messageId, $paginate: false })).unwrap();
      
      // Extract the message from the result array
      const message = Array.isArray(messageResult) ? messageResult[0] : (messageResult as any)?.data?.[0];
      
      if (!message) {
        console.warn(`⚠️ [BACKUP_RESTORE] Message not found in ai-legal-messages: ${messageId}, checking case-documents-search...`);
        
        // Try searching in case-documents-search as fallback
        // The messageId passed might actually be a case-documents-search ID
        try {
          const searchRecord = await feathers.service('case-documents-search').get(messageId);
          if (searchRecord) {
            console.log(`✅ [BACKUP_RESTORE] Found search record: ${messageId}`);
            return processRecord(searchRecord, true);
          }
        } catch (error) {
          console.warn(`⚠️ [BACKUP_RESTORE] Record not found in case-documents-search either: ${messageId}`);
        }
        
        return null;
      }
      
      return processRecord(message, false);
    } catch (error) {
      console.error('[BACKUP_RESTORE] Error restoring progress:', error);
      return null;
    }
    
    // Helper to process either message or search record
    function processRecord(record: any, isSearchRecord: boolean) {
      const queryId = record.metadata?.queryId;
      
      if (!queryId) {
        console.warn(`⚠️ [BACKUP_RESTORE] No queryId in record metadata`);
        return null;
      }
      
      // Check status
      if (record.metadata?.status !== 'active' && record.status !== 'active') {
        console.log(`✅ [BACKUP_RESTORE] Record is not active (status: ${record.metadata?.status || record.status}), no restoration needed`);
        return null;
      }
      
      // Initialize progress state
      dispatch(startProgress({
        queryId,
        chatId: record.chatId,
        caseId: record.caseId
      }));
      
      // Restore last progress update if available
      if (record.lastProgressUpdate) {
        const { stage, progress, details, timestamp } = record.lastProgressUpdate;
        dispatch(updateProgress({
          queryId,
          overallProgress: progress,
          currentStage: stage,
          stageDetails: details,
          status: 'active',
          chatId: record.chatId,
          caseId: record.caseId,
          advancesProgress: true
        }));
        console.log(`✅ [BACKUP_RESTORE] Restored progress: ${progress}%, stage: ${stage}`);
      }
      
      // Restore feed updates from recentFeedUpdates array
      if (record.recentFeedUpdates && record.recentFeedUpdates.length > 0) {
        console.log(`✅ [BACKUP_RESTORE] Restoring ${record.recentFeedUpdates.length} feed updates from DB array`);
        
        record.recentFeedUpdates.forEach((feedItem: any, index: number) => {
          const { id: feedId, message: feedMessage, timestamp: feedTimestamp } = feedItem;
          
          dispatch(updateProgress({
            queryId,
            overallProgress: record.lastProgressUpdate?.progress || 0,
            currentStage: record.lastProgressUpdate?.stage || 'initialization',
            stageDetails: feedMessage,
            status: 'active',
            chatId: record.chatId,
            caseId: record.caseId,
            advancesProgress: false,
            feedId,
            feedTimestamp: feedTimestamp ? new Date(feedTimestamp).getTime() : Date.now()
          }));
        });
      } else if (record.lastFeedUpdate) {
        const { message: feedMessage, timestamp } = record.lastFeedUpdate;
        dispatch(updateProgress({
          queryId,
          overallProgress: record.lastProgressUpdate?.progress || 0,
          currentStage: record.lastProgressUpdate?.stage || 'initialization',
          stageDetails: feedMessage,
          status: 'active',
          chatId: record.chatId,
          caseId: record.caseId,
          advancesProgress: false
        }));
      }
      
      return { queryId, restored: true };
    }
  }
);

// ✅ Poll progress from DB (used when MQTT is disconnected or slow)
export const pollProgressFromDB = createAsyncThunk(
  'simpleMqtt/pollProgress',
  async ({ queryId, servicePath = 'ai-legal-messages' }: { queryId: string, servicePath?: string }, { dispatch }) => {
    try {
      console.log(`🔍 [POLL_PROGRESS] Polling DB for queryId: ${queryId} via ${servicePath}`);
      
      const result = await feathers.service(servicePath as any).find({
        query: {
          'metadata.queryId': queryId,
          $limit: 1
        }
      });
      
      const records = Array.isArray(result) ? result : (result as any).data || [];
      const record = records[0];
      
      if (record) {
        const status = record.status || record.metadata?.status;
        
        if (status === 'completed') {
          dispatch(completeProgress({ queryId }));
          console.log(`✅ [POLL_PROGRESS] Query completed: ${queryId}`);
          return true;
        }
        
        if (status === 'error') {
          dispatch(completeProgress({ queryId }));
          console.log(`❌ [POLL_PROGRESS] Query failed: ${queryId}`);
          return false;
        }
        
        // Update with last known progress
        if (record.lastProgressUpdate) {
          dispatch(updateProgress({
            queryId,
            overallProgress: record.lastProgressUpdate.progress,
            currentStage: record.lastProgressUpdate.stage,
            stageDetails: record.lastProgressUpdate.details,
            status: 'active',
            chatId: record.chatId,
            caseId: record.caseId,
            advancesProgress: true
          }));
        }
        
        // Check for new feed updates in the array
        if (record.recentFeedUpdates && record.recentFeedUpdates.length > 0) {
          record.recentFeedUpdates.forEach((feedItem: any) => {
            dispatch(updateProgress({
              queryId,
              overallProgress: record.lastProgressUpdate?.progress || 0,
              currentStage: record.lastProgressUpdate?.stage || 'initialization',
              stageDetails: feedItem.message,
              status: 'active',
              chatId: record.chatId,
              caseId: record.caseId,
              advancesProgress: false,
              feedId: feedItem.id,
              feedTimestamp: new Date(feedItem.timestamp).getTime()
            }));
          });
        }
      }
      return false;
    } catch (error) {
      console.error('[POLL_PROGRESS] Error polling progress:', error);
      return false;
    }
  }
);